import {CustomerServicePromiseClient} from '@nxcr-org/web-api-interface/lib/gateway_service_grpc_web_pb'
import {NexusServicePromiseClient} from '@nxcr-org/web-api-interface/lib/nexus_service_grpc_web_pb'

import {oktaAuth, OktaAuthInterceptor} from '../../okta/config'
import {Env} from '../fleet-management/api/env'
import {
  GetAllCustomersByPhoneRequest,
  GetCustomerByIDRequest,
  ListCustomersRequest,
  UpdateCustomerWithFakePhoneRequest,
  UpsertAddressesRequest,
} from '@nxcr-org/web-api-interface/lib/customer_service_pb'
import {OrderParams, PaginationParams, QueryParams, SearchParams,} from '@nxcr-org/web-api-interface/lib/web_pb'
import {GetCustomerImagesRequest,} from '@nxcr-org/web-api-interface/lib/nexus_service_pb'
import {Address} from '@nxcr-org/web-api-interface/lib/domain_pb'
import toast from 'react-hot-toast'

export function buildQueryListObj(
  params: QueryParams.AsObject[]
): QueryParams[] {
  return params.map((queryParam) => {
    const request = new QueryParams()
    request.setProperty(queryParam.property)
    request.setValue(queryParam.value)
    request.setModifier(queryParam.modifier)
    return request
  })
}

export function buildOrderListObj(
  params: OrderParams.AsObject[]
): OrderParams[] {
  return params.map((queryParam) => {
    const request = new OrderParams()
    request.setProperty(queryParam.property)
    request.setDirection(queryParam.direction)
    return request
  })
}

export function buildPaginationObj(
  params: PaginationParams.AsObject
): PaginationParams {
  const request = new PaginationParams()

  request.setLimit(params?.limit)
  request.setOffset(params?.offset)

  return request
}

function getCustomerService(): CustomerServicePromiseClient {
  const idToken = oktaAuth.getIdToken()
  const target = Env.endpoints.fleet

  return new CustomerServicePromiseClient(target, null, {
    unaryInterceptors: [new OktaAuthInterceptor(idToken)],
    'grpc-node.max_session_memory': 1000,
    'grpc-node.max_send_message_length': 1024 * 1024 * 100,
    'grpc-node.max_receive_message_length': 1024 * 1024 * 100,
  })
}

function getNexusService(): NexusServicePromiseClient {
  const idToken = oktaAuth.getIdToken()
  const target = Env.endpoints.fleet

  return new NexusServicePromiseClient(target, null, {
    unaryInterceptors: [new OktaAuthInterceptor(idToken)],
    'grpc-node.max_session_memory': 1000,
    'grpc-node.max_send_message_length': 1024 * 1024 * 100,
    'grpc-node.max_receive_message_length': 1024 * 1024 * 100,
  })
}

export function UpdateCustomerWithFakePhone(customerId) {
  const client = getCustomerService()
  const request = new UpdateCustomerWithFakePhoneRequest()
  request.setCustomerId(customerId)
  return client.updateCustomerWithFakePhone(request).then((res) => {
    return res.toObject()
  })
}

export function getCustomerById(customerId) {
  const client = getCustomerService()
  const request = new GetCustomerByIDRequest()
  request.setCustomerId(customerId)
  return client.getCustomerByID(request).then((res) => {
    return res.toObject()
  })
}

export function getCustomerImages(customerId) {
  const client = getNexusService()
  const request = new GetCustomerImagesRequest()
  request.setCustomerId(customerId)
  return client.getCustomerImages(request).then((res) => {
    return res.toObject()
  })
}

export function ListCustomers({ searchParam }) {
  const client = getCustomerService()
  const request = new ListCustomersRequest()
  const searchParamsObj = new SearchParams()

  searchParamsObj.setQueryList(buildQueryListObj(searchParam?.query || []))
  searchParamsObj.setOrderList(buildOrderListObj(searchParam?.order || []))
  searchParamsObj.setPagination(buildPaginationObj(searchParam?.pagination))
  request.setSearchParam(searchParamsObj)
  return client.listCustomers(request).then((res) => {
    return res.toObject()
  })
}

export async function editCustomerAddresses(customer_id: string, addressObj) {
  try {
    const client = getCustomerService()
    const request = new UpsertAddressesRequest()
    const addresses = [addressObj].map((addr) => {
      const address = new Address()
      address.setStreet(addr.street)
      address.setStreet2(addr.street2)
      address.setState(addr.state)
      address.setCity(addr.city)
      address.setPostal(addr.postal)
      address.setAddressType(addr.addressType)
      address.setDeleted(false)
      address.setId(addr.id)
      return address
    })
    request.setCustomerId(customer_id)
    request.setAddressesList(addresses)
    return await client.upsertAddresses(request)
  } catch (error) {
    toast.error(error.message)
  }
}

export function getAllCustomersByPhone(phoneNumber: string) {
  try {
    const client = getNexusService()
    // Note: This API call may need to be implemented on the backend
    // For now, we'll attempt to call it and gracefully handle any errors
    if (typeof client.getAllCustomersByPhone === 'function') {
      const request = new GetAllCustomersByPhoneRequest()
      request.setPhone(phoneNumber)
      return client.getAllCustomersByPhone(request).then((res) => {
        return res.toObject ? res.toObject() : res
      }).catch((error) => {
        console.error('getAllCustomersByPhone error:', error)
        return { customersList: [] }
      })
    }
  } catch (error) {
    console.error('getAllCustomersByPhone client error:', error)
    return Promise.resolve({ customersList: [] })
  }
}
